"use client"

import { formatNewsPubTimeStable } from "@/common/utils/formatNewsTime"
import { getLinkPageUrl, isSafeUrl, titleFormat } from "@repo/shared"
import { useCallback, useState } from "react"

import type { PreciousMetalNewsFilter } from "../../../../../services/newscenter"
import type { NewsItem } from "../../../../../services/newscenter/interface"
import { loadMoreNews } from "./actions"
import Style from "./LatestNews.module.scss"

interface LatestNewsClientProps {
	initialList: NewsItem[]
	locale: string
	excludeIds: number[]
	firstPageRest: NewsItem[]
	metal: PreciousMetalNewsFilter
}

export default function LatestNewsClient({
	initialList,
	locale,
	excludeIds,
	firstPageRest,
	metal,
}: LatestNewsClientProps) {
	const [list, setList] = useState<NewsItem[]>(initialList)
	const [page, setPage] = useState(1)
	const [loading, setLoading] = useState(false)
	const [noMore, setNoMore] = useState(initialList.length < 10)

	const handleViewMore = useCallback(async () => {
		if (loading || noMore) return
		setLoading(true)
		const nextPage = page + 1
		const excludeAllIds = [...excludeIds, ...list.map((item) => item.id)]
		const res = await loadMoreNews(locale, nextPage, excludeAllIds, metal)
		setLoading(false)
		if (res.success && res.data?.length) {
			const extra = page === 1 ? firstPageRest : []
			const merged = [...extra, ...res.data!]
			setList((prev) => [...prev, ...merged])
			setPage(nextPage)
			if (res.data.length < 20) setNoMore(true)
		} else {
			setNoMore(true)
		}
	}, [locale, page, excludeIds, list, loading, noMore, firstPageRest, metal])

	return (
		<div className={Style.listWrap}>
			<ul className={Style.list}>
				{list.map((item) => {
					const isSafe = isSafeUrl(item.source_url)
					const href = isSafe
						? getLinkPageUrl(item.source_url, locale) + `-${titleFormat(item.title, { lower: true })}`
						: getLinkPageUrl(item.source_url, locale)
					const timeStr = formatNewsPubTimeStable(item.pub_time * 1000)
					return (
						<li key={item.id} className={Style.listItem}>
							<a href={href} target="_blank" rel="noopener noreferrer" className={Style.listLink}>
								{item.title}
							</a>
							<span className={Style.listTime}>{timeStr}</span>
						</li>
					)
				})}
			</ul>
			{!noMore && (
				<button type="button" className={Style.viewMore} onClick={handleViewMore} disabled={loading}>
					{loading ? "Loading..." : "View More"}
				</button>
			)}
		</div>
	)
}
